/** Strings of the billing screens (reports, report builder, dialogs) and the AI spend page: `billingEn` is the source, `billingCs` must have the same keys. */
export const billingEn = {
  billing: {
    status: {
      draft: "draft",
      processing: "processing",
      sent: "sent",
      paid: "paid",
      failed: "failed",
      overdue: "overdue",
    },
    new: {
      back: "Back to billing",
      title: "Generate report",
      subtitle:
        'Build a draft "výkaz" for a customer and period — one line per main task plus the costs you pick.',
    },
    list: {
      title: "Billing",
      subtitle:
        "The invoice-batch ledger. Open a report to review its lines and issue the invoice; its payment comes from the accounting.",
      generate: "Generate report",
      totalPaid: "Total Paid",
      pending: "Pending",
      totalBatches: "Total Batches",
      searchPlaceholder: "Search by customer or invoice number…",
      filterStatus: "Filter by status",
      filterCustomer: "Filter by customer",
      allStatuses: "All statuses",
      allCustomers: "All customers",
      empty: "No invoice batches found.",
      col: {
        customer: "Customer",
        period: "Period",
        status: "Status",
        amount: "Amount",
        invoice: "Invoice #",
        items: "Items",
        created: "Created",
      },
    },
    detail: {
      back: "Back to billing",
      period: "Period",
      invoice: "Invoice",
      fakturoidInvoice: "Fakturoid invoice {{number}}",
      total: "Total",
      paymentStatus: "Payment status",
      issued: "Issued:",
      due: "Due:",
      received: "Received:",
      receivedOf: "{{paid}} of {{total}}",
      paid: "Paid:",
      notYet: "not yet",
      noItems: "This report has no line items.",
      workedHoursPdf: "Worked hours PDF",
      hoursSummaryPdf: "Hours summary PDF",
      submit: "Submit to invoicing",
      downloadPdf: "Download PDF",
      refreshing: "Refreshing",
      refresh: "Refresh status",
      propagating: "Propagating",
      propagate: "Propagate changes",
      deleteDraft: "Delete draft",
      history: "History",
      system: "System",
      cancel: "Cancel",
      submitting: "Submitting",
      createInvoice: "Create invoice",
      deleting: "Deleting",
      col: {
        line: "Line",
        hours: "Hours",
        rate: "Rate",
        amount: "Amount",
      },
      toast: {
        submitted: "Submitted to invoicing",
        invoice: "Invoice {{number}}",
        invoiceEmailed: "Invoice {{number}} — PDF emailed",
        invoiceCreated: "created",
        submitFailed: "Submit failed",
        refreshed: "Status refreshed",
        invoicePaid: "Invoice is paid.",
        invoiceStatus: "Invoice is {{status}}.",
        refreshFailed: "Refresh failed",
        propagated: "Changes propagated to Fakturoid",
        propagateFailed: "Propagate failed",
        deleted: "Draft report deleted",
        deleteFailed: "Delete failed",
      },
      submitDialog: {
        title: "Submit to invoicing?",
        description:
          "This issues a real invoice for {{customer}} in the accounting and emails its PDF. The covered time entries are marked invoiced and follow the invoice once it is paid. A mistake is corrected by a credit note.",
      },
      deleteDialog: {
        title: "Delete this draft report?",
        description:
          "The draft and its lines are removed. The covered time entries are released back so a later report can bill them. This cannot be undone.",
      },
      item: {
        editFailed: "Edit failed",
        removed: "Line removed",
        removeFailed: "Remove failed",
        unpaired_one: "{{count}} time entry unpaired",
        unpaired_few: "{{count}} time entries unpaired",
        unpaired_other: "{{count}} time entries unpaired",
        unpairFailed: "Unpair failed",
        cost: "cost",
        paired_one: "{{count}} time entry paired",
        paired_few: "{{count}} time entries paired",
        paired_other: "{{count}} time entries paired",
        unpair: "Unpair",
        descriptionLabel: "Line description for {{name}}",
        hoursLabel: "Hours for {{name}}",
        rateLabel: "Rate for {{name}}",
        amountLabel: "Amount for {{name}}",
        removeLabel: "Remove line {{name}}",
      },
    },
    addToDraft: {
      title: "Send to existing report",
      loadFailed: "Couldn't load reports",
      error: "Error",
      added_entries_one: "{{count}} entry added to the report",
      added_entries_few: "{{count}} entries added to the report",
      added_entries_other: "{{count}} entries added to the report",
      added_workItems_one: "{{count}} work item added to the report",
      added_workItems_few: "{{count}} work items added to the report",
      added_workItems_other: "{{count}} work items added to the report",
      addFailed: "Couldn't add to report",
      description_entries_one: "Add {{count}} entry to an open draft report",
      description_entries_few: "Add {{count}} entries to an open draft report",
      description_entries_other: "Add {{count}} entries to an open draft report",
      description_workItems_one: "Add {{count}} work item to an open draft report",
      description_workItems_few: "Add {{count}} work items to an open draft report",
      description_workItems_other: "Add {{count}} work items to an open draft report",
      forCustomer: "for {{customer}}",
      loading: "Loading reports…",
      lines_one: "{{count}} line",
      lines_few: "{{count}} lines",
      lines_other: "{{count}} lines",
      adding: "Adding…",
      empty: "No open draft reports for this customer. Use “Create report” instead.",
    },
    pair: {
      title: "Pair to issued invoice",
      loadFailed: "Couldn't load invoices",
      error: "Error",
      paired_one: "{{count}} time entry paired to the invoice",
      paired_few: "{{count}} time entries paired to the invoice",
      paired_other: "{{count}} time entries paired to the invoice",
      pairFailed: "Couldn't pair entries",
      loadLinesFailed: "Couldn't load the invoice lines",
      description_one:
        "Link {{count}} entry to an invoice already issued. The invoice amounts don't change.",
      description_few:
        "Link {{count}} entries to an invoice already issued. The invoice amounts don't change.",
      description_other:
        "Link {{count}} entries to an invoice already issued. The invoice amounts don't change.",
      descriptionFor_one:
        "Link {{count}} entry to an invoice already issued to {{customer}}. The invoice amounts don't change.",
      descriptionFor_few:
        "Link {{count}} entries to an invoice already issued to {{customer}}. The invoice amounts don't change.",
      descriptionFor_other:
        "Link {{count}} entries to an invoice already issued to {{customer}}. The invoice amounts don't change.",
      loading: "Loading invoices…",
      loadingShort: "Loading…",
      pairing: "Pairing…",
      noFlatLine:
        "This invoice has no flat line to pair hours to; its lines are billed from their own hours or work items.",
      invoice: "Invoice",
      empty: "No issued invoices for this customer.",
    },
    builder: {
      title: "Generate report",
      description:
        "Pick a customer and period, then choose which fixed-price work items, costs and ad-hoc lines to bill. Worked hours on the customer's hours-mode work items are always included.",
      step1: "1 · Customer & period",
      customer: "Customer",
      selectCustomer: "— select customer —",
      periodFrom: "Period from",
      periodTo: "Period to",
      step2: "2 · Fixed-price work items",
      fixedHint:
        "Done work is pre-ticked; untick anything you don't want on this report. Their delivery price is billed as a line, their hours are not billed separately. \"Already invoiced\" drops a work item for good — it won't be re-offered on the next report either.",
      noFixed: "No fixed-price work items for this customer.",
      include: "Include {{name}}",
      done: "Done",
      alreadyInvoiced: "Already invoiced",
      alreadyInvoicedLower: "already invoiced",
      step3: "3 · Costs to include",
      costsHint: "Tick the one-off costs to put on this invoice. Each is billed once.",
      cancel: "Cancel",
      addCost: "Add cost",
      noCosts: "No costs for this customer yet.",
      step4: "4 · Individual items",
      individualHint: "Ad-hoc lines billed straight on this report.",
      addItem: "Add item",
      itemDescription: "Description",
      itemDescriptionLabel: "Individual item {{n}} description",
      itemAmountLabel: "Individual item {{n}} amount",
      itemRemoveLabel: "Remove individual item {{n}}",
      generating: "Generating",
      generated: "Report generated",
      generateFailed: "Report failed",
      pickCustomer: "Pick a customer first",
      markedInvoiced: 'Marked "{{title}}" as already invoiced',
      markFailed: "Couldn't mark as invoiced",
      error: "Error",
      costAdded: "Cost added",
      costAddFailed: "Could not add cost",
      costInvalid: "Enter a label and a positive amount",
      costLabel: "Label",
      costLabelPlaceholder: "e.g. Domain renewal",
      costAmount: "Amount",
      adding: "Adding",
      add: "Add",
    },
  },
  netlionTokens: {
    settingsTitle: "Netlion tokens",
    settingsHint:
      "Company defaults for billing agent work: a surcharge in CZK per 1 000 000 tokens of each type on top of the list price, and the monthly cap per customer. A customer can override each value on its edit page.",
    customerTitle: "Netlion tokens",
    customerHint:
      "Surcharges in CZK per 1 000 000 tokens and the monthly cap for this customer. An empty field uses the company default from the accounting settings (shown greyed out).",
    field: {
      tokenInputSurcharge: "Input (CZK / 1M)",
      tokenOutputSurcharge: "Output (CZK / 1M)",
      tokenCacheWriteSurcharge: "Cache write (CZK / 1M)",
      tokenCacheReadSurcharge: "Cache read (CZK / 1M)",
      tokenMonthlyCapCzk: "Monthly cap (CZK)",
    },
  },
  aiSpend: {
    usage: {
      fiveHour: "5-hour limit",
      sevenDay: "Weekly limit",
      minutes: "{{min}} min",
      hoursMinutes: "{{h}} h {{min}} min",
      daysHours: "{{d}} d {{h}} h",
      noReading: "no reading yet",
      reset: "reset",
      resetsIn: "resets in {{time}}",
    },
    hidden: {
      ui_session: "LiteLLM UI sign-in",
      expired: "expired",
      blocked: "blocked",
    },
    credentials: {
      kinds: {
        gateway_key: "Gateway key",
        claude_token: "Claude subscription",
        codex_login: "ChatGPT subscription (Codex)",
      },
      secretHelp: {
        gateway_key: "A LiteLLM virtual key from the gateway.",
        claude_token:
          "From `claude setup-token`, run by the subscription's owner. Each run gives a new token; tokens of one account can be merged here afterwards.",
        codex_login:
          "Contents of $CODEX_HOME/auth.json after `export CODEX_HOME=$(mktemp -d)` and `npx -y @openai/codex login --device-auth`. Don't use that login anywhere else: Hub keeps refreshing it.",
      },
      rename: "Rename",
      renameFailed: "Couldn't rename it.",
      reread: "Refresh plan",
      rereadHint:
        "Reads the limits again now; after a plan upgrade the agents get back to work without waiting for the reset",
      rereading: "Reading…",
      rereadDone: "Limits read again.",
      rereadLifted: "There's room again, the agents on it are back at work.",
      rereadFailed: "Couldn't read the limits.",
      name: "Name",
      tokenRefused: "Token refused",
      waitingUntil: "Waiting until",
      active: "Active",
      noAgentUses: "No agent uses it",
      paused: "paused",
      saveFailed: "Couldn't save.",
      saved: "Saved.",
      merged: "Merged.",
      deleted: "Deleted.",
      plan: "Plan",
      planPlaceholder: "e.g. Max 20×",
      renewsOn: "Renews on (monthly)",
      newSecret: "New secret",
      keepSecret: "leave empty to keep it",
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      deleteConfirm: "Delete {{name}}? The secret is gone for good.",
      sameSubscriptionAs: "Same subscription as",
      mergeInto: "Merge into",
      pickOne: "— pick one —",
      mergeIntoIt: "Merge into it",
      mergeHint: "Its agents move over and keep that one's token; this one goes.",
      mergeFailed: "Couldn't merge.",
      sameAccountHint:
        "Probably the same subscription as <1>{{name}}</1>: the weekly limit resets at the same time. Merging moves its agents there.",
      merge: "Merge",
      planUnknown: "plan unknown",
      renews: "renews",
      renewalUnknown: "renewal unknown",
      monthSummary_one: "{{count}} run this month · API equivalent {{cost}}",
      monthSummary_few: "{{count}} runs this month · API equivalent {{cost}}",
      monthSummary_other: "{{count}} runs this month · API equivalent {{cost}}",
      read: "read",
      edit: "Edit",
      thisMonth: "{{amount}} this month",
      lastMonthRuns_one: "{{amount}} last month · {{count}} run",
      lastMonthRuns_few: "{{amount}} last month · {{count}} runs",
      lastMonthRuns_other: "{{amount}} last month · {{count}} runs",
      runsThisMonth_one: "{{count}} run · ~{{cost}} this month",
      runsThisMonth_few: "{{count}} runs · ~{{cost}} this month",
      runsThisMonth_other: "{{count}} runs · ~{{cost}} this month",
      added: "Added. Pick it for an agent on its page.",
      addFailed: "Couldn't add it.",
      kind: "Kind",
      namePlaceholderKey: "Name, e.g. Hubert",
      namePlaceholderSub: "Name, e.g. Claude Max (team)",
      secret: "Secret",
      secretPlaceholder: "Stored encrypted, never shown again",
      add: "Add",
      subscriptionsTitle: "Subscriptions",
      subscriptionsIntro:
        "Flat-price Claude and ChatGPT subscriptions the agents run on. An agent picks one on its page; a reached limit or a refused token holds every agent on it. Click a name to rename it.",
      noSubscription: "No subscription connected.",
      keysTitle: "Agent keys",
      keysIntro:
        "Gateway keys the agents pay with — metered, also next to a subscription (embeddings, models a subscription can't run). Click a name to rename it.",
      noKey: "No gateway key stored in Hub.",
    },
    page: {
      title: "AI Spend",
      subtitle:
        "What every gateway key burns, in CZK — and one-click re-invoicing of a closed month to the mapped customer (with markup).",
      gatewayDown: "The LiteLLM gateway didn't answer — check LITELLM_URL / LITELLM_ADMIN_KEY.",
      loadFailed: "Couldn't load AI spend.",
      mappingSaved: "Mapping saved",
      mappingFailed: "Couldn't save the mapping.",
      charged: "Charged {{amount}} to {{customer}}",
      chargedHint: "The cost line is ready to be picked into the monthly report.",
      chargeFailed: "Couldn't create the charge.",
      chargeRemoved: "Charge removed",
      chargeRemoveFailed: "Couldn't remove the charge.",
      col: {
        key: "Key",
        thisMonth: "This month",
        lastMonth: "Last month",
        lifetime: "Lifetime",
        customer: "Customer",
        markup: "Markup %",
        reinvoice: "Re-invoice",
      },
      budget: "/ budget {{amount}}",
      internal: "— internal —",
      customerFor: "Customer for {{key}}",
      markupFor: "Markup for {{key}}",
      saveMapping: "Save mapping",
      chargeMonth: "Charge {{month}}",
      undo: "undo",
      rateFallback: "fallback",
      gatewayOff:
        "The LiteLLM gateway isn't configured. Set <1>LITELLM_URL</1> and <3>LITELLM_ADMIN_KEY</3> (a key created with <5>get_spend_routes</5> permission is enough — don't use the master key).",
      monthAllKeys: "This month, all keys",
      lastMonthWith: "Last month ({{month}})",
      dailySpend: "Daily spend (60 days, CZK)",
      syncedAt: "Gateway spend as of <1/>, read every 30 minutes.",
      firstRead:
        "The first read of the gateway's spend is running (a few minutes); the numbers fill in after it.",
      tab: {
        clients: "Client keys",
        agents: "Agent keys",
        work: "Agent work",
        limits: "Limit usage",
        subscriptions: "Subscriptions",
        prices: "Prices",
      },
      noKeys: "No gateway keys yet.",
      hideKeys_one: "Hide {{count}} key LiteLLM doesn't list",
      hideKeys_few: "Hide {{count}} keys LiteLLM doesn't list",
      hideKeys_other: "Hide {{count}} keys LiteLLM doesn't list",
      showKeys_one: "Show {{count}} hidden: {{reasons}}, no spend in two months",
      showKeys_few: "Show {{count}} hidden: {{reasons}}, no spend in two months",
      showKeys_other: "Show {{count}} hidden: {{reasons}}, no spend in two months",
      clientsNote:
        "Every gateway key except the agents' own (those are under Agent keys). Charging a month creates a one-off customer cost („AI služby … {{month}}\") — pick it into the monthly report on the Billing page as usual. Undo works until the cost lands on an invoice. Key management (creating keys, hard budgets) stays in the <1>LiteLLM UI</1>.",
      notConfigured: "The gateway isn't configured.",
    },
    work: {
      title: "Agent work per project",
      month: "Month",
      intro:
        "What the agents' runs cost per project, priced from the price list at the time of the run. Runs on a subscription are an estimate (the subscription itself is flat), runs on a gateway key are what the gateway bills, minus calls outside a run like embeddings, which is why Agent keys can show more. The Netlion tokens column is what the customer is billed: completed runs outside fixed-price tickets at list price × the CNB rate of the month's last day, plus a surcharge per 1M tokens of each type. Charging adds a one-off customer cost („Netlion tokeny — projekt — {{month}}\"); projects without a customer can't be charged.",
      empty: "No agent work on projects in that month.",
      charged: "Charged",
      chargeFailed: "Couldn't charge",
      withdrawn: "Charge withdrawn",
      tryAgain: "Try again.",
      undoFailed: "Couldn't undo",
      invoiced: "invoiced",
      undo: "Undo",
      charge: "Charge",
      noCustomer: "No customer",
      col: {
        project: "Project",
        runs: "Runs",
        gateway: "Gateway",
        subscription: "Subscription (est.)",
        total: "Total",
        tokens: "Netlion tokens",
        reinvoice: "Re-invoice {{month}}",
      },
      tokensHint: "{{runs}} billable runs: {{list}} list price + {{surcharge}} surcharges",
      cap: {
        title: "Monthly cap of Netlion tokens",
        warning: "80 % of cap",
        over: "Over cap",
        line: "{{customer}}: {{amount}} of {{cap}}",
        hint: "Charging is not blocked. Going over the cap needs the client's prior consent (an e-mail is enough).",
      },
    },
    limits: {
      title: "What eats the subscription limits",
      period: "Period",
      days_one: "Last {{count}} day",
      days_few: "Last {{count}} days",
      days_other: "Last {{count}} days",
      intro:
        "Runs on a flat plan, per subscription, priced as if they went through the API. The plan bills a flat fee, but its limits shrink roughly by this amount, so the bigger the share, the more of the limit it eats. Runs from before 27 Oct 2026 don't record their subscription and are grouped apart. Gateway runs paid per token are not a limit and are left out.",
      empty: "No runs on a subscription in this period.",
      pool: {
        subscription: "Claude",
        codex: "ChatGPT",
      },
      unrecorded: {
        subscription: "Claude, subscription not recorded",
        codex: "ChatGPT, subscription not recorded",
      },
      poolTotal_one: "{{amount}} across {{count}} run",
      poolTotal_few: "{{amount}} across {{count}} runs",
      poolTotal_other: "{{amount}} across {{count}} runs",
      tickets: "Costliest tickets",
      ticketsIntro: "Many runs on one ticket usually mean returns, retries or long plan rounds.",
      col: {
        agent: "Agent",
        model: "Model",
        runs: "Runs",
        share: "Share",
        cost: "Est. cost",
        wasted: "Failed or cut off",
        ticket: "Ticket",
        agents: "Agents",
      },
      wasted_one: "{{amount}} in {{count}} run",
      wasted_few: "{{amount}} in {{count}} runs",
      wasted_other: "{{amount}} in {{count}} runs",
    },
    totals: {
      allRuns: "All agent runs",
      runs_one: "{{count}} run",
      runs_few: "{{count}} runs",
      runs_other: "{{count}} runs",
      gateway: "Gateway",
      perToken: "billed per token",
      subscription: "Subscription",
      estimate: "estimate, flat price",
      onProjects: "On projects",
      canReinvoice: "can be re-invoiced",
      noProject: "No project",
      noProjectRuns_one: "{{count}} run (mail, scans…)",
      noProjectRuns_few: "{{count}} runs (mail, scans…)",
      noProjectRuns_other: "{{count}} runs (mail, scans…)",
    },
    prices: {
      title: "Model prices",
      refreshing: "Refreshing…",
      refreshNow: "Refresh now",
      refreshed_one: "Refreshed {{count}} model price from the gateway.",
      refreshed_few: "Refreshed {{count}} model prices from the gateway.",
      refreshed_other: "Refreshed {{count}} model prices from the gateway.",
      refreshFailed: "Couldn't refresh the model prices.",
      intro:
        "USD per million tokens, read from the LiteLLM gateway's own catalogue and refreshed daily — this is what every agent run on this page is costed with. A model the gateway doesn't price falls back to Hub's built-in rates.",
      noneLoaded: "No prices loaded yet — the daily job hasn't run. Use Refresh now.",
      gatewayOff:
        "The LiteLLM gateway isn't configured, so Hub is using its built-in fallback rates.",
      empty: "No model prices stored.",
      lastRefreshed: "Last refreshed <1/>",
      col: {
        model: "Model",
        input: "Input",
        output: "Output",
        cacheWrite: "Cache write",
        cacheRead: "Cache read",
      },
    },
  },
};

export const billingCs: typeof billingEn = {
  billing: {
    status: {
      draft: "koncept",
      processing: "zpracovává se",
      sent: "odesláno",
      paid: "zaplaceno",
      failed: "selhalo",
      overdue: "po splatnosti",
    },
    new: {
      back: "Zpět na fakturaci",
      title: "Vytvořit výkaz",
      subtitle:
        "Sestavte koncept výkazu pro zákazníka a období — jeden řádek za každý hlavní úkol plus vybrané náklady.",
    },
    list: {
      title: "Fakturace",
      subtitle:
        "Přehled fakturačních dávek. Otevřete výkaz, zkontrolujte jeho řádky a vystavte fakturu; platba se přebírá z účetnictví.",
      generate: "Vytvořit výkaz",
      totalPaid: "Celkem zaplaceno",
      pending: "Čeká na zaplacení",
      totalBatches: "Dávek celkem",
      searchPlaceholder: "Hledat podle zákazníka nebo čísla faktury…",
      filterStatus: "Filtrovat podle stavu",
      filterCustomer: "Filtrovat podle zákazníka",
      allStatuses: "Všechny stavy",
      allCustomers: "Všichni zákazníci",
      empty: "Žádné fakturační dávky nenalezeny.",
      col: {
        customer: "Zákazník",
        period: "Období",
        status: "Stav",
        amount: "Částka",
        invoice: "Č. faktury",
        items: "Položky",
        created: "Vytvořeno",
      },
    },
    detail: {
      back: "Zpět na fakturaci",
      period: "Období",
      invoice: "Faktura",
      fakturoidInvoice: "Faktura ve Fakturoidu {{number}}",
      total: "Celkem",
      paymentStatus: "Stav platby",
      issued: "Vystaveno:",
      due: "Splatnost:",
      received: "Přijato:",
      receivedOf: "{{paid}} z {{total}}",
      paid: "Zaplaceno:",
      notYet: "zatím ne",
      noItems: "Tento výkaz nemá žádné řádky.",
      workedHoursPdf: "PDF odpracovaných hodin",
      hoursSummaryPdf: "PDF souhrnu hodin",
      submit: "Předat k fakturaci",
      downloadPdf: "Stáhnout PDF",
      refreshing: "Obnovuje se",
      refresh: "Obnovit stav",
      propagating: "Přenáší se",
      propagate: "Přenést změny",
      deleteDraft: "Smazat koncept",
      history: "Historie",
      system: "Systém",
      cancel: "Zrušit",
      submitting: "Předává se",
      createInvoice: "Vystavit fakturu",
      deleting: "Maže se",
      col: {
        line: "Řádek",
        hours: "Hodiny",
        rate: "Sazba",
        amount: "Částka",
      },
      toast: {
        submitted: "Předáno k fakturaci",
        invoice: "Faktura {{number}}",
        invoiceEmailed: "Faktura {{number}} — PDF odesláno e-mailem",
        invoiceCreated: "vytvořena",
        submitFailed: "Předání se nezdařilo",
        refreshed: "Stav obnoven",
        invoicePaid: "Faktura je zaplacená.",
        invoiceStatus: "Stav faktury: {{status}}.",
        refreshFailed: "Obnovení se nezdařilo",
        propagated: "Změny přenesené do Fakturoidu",
        propagateFailed: "Přenesení změn se nezdařilo",
        deleted: "Koncept výkazu smazán",
        deleteFailed: "Smazání se nezdařilo",
      },
      submitDialog: {
        title: "Předat k fakturaci?",
        description:
          "V účetnictví se vystaví skutečná faktura pro zákazníka {{customer}} a její PDF se odešle e-mailem. Zahrnuté záznamy o čase se označí jako vyfakturované a po zaplacení faktury ji následují. Chyba se opravuje dobropisem.",
      },
      deleteDialog: {
        title: "Smazat tento koncept výkazu?",
        description:
          "Koncept i jeho řádky se odstraní. Zahrnuté záznamy o čase se uvolní, aby je mohl vyfakturovat pozdější výkaz. Tuto akci nelze vrátit.",
      },
      item: {
        editFailed: "Úprava se nezdařila",
        removed: "Řádek odstraněn",
        removeFailed: "Odstranění se nezdařilo",
        unpaired_one: "Spárování zrušeno u {{count}} záznamu o čase",
        unpaired_few: "Spárování zrušeno u {{count}} záznamů o čase",
        unpaired_other: "Spárování zrušeno u {{count}} záznamů o čase",
        unpairFailed: "Zrušení spárování se nezdařilo",
        cost: "náklad",
        paired_one: "{{count}} záznam o čase spárován",
        paired_few: "{{count}} záznamy o čase spárovány",
        paired_other: "{{count}} záznamů o čase spárováno",
        unpair: "Zrušit spárování",
        descriptionLabel: "Popis řádku {{name}}",
        hoursLabel: "Hodiny pro {{name}}",
        rateLabel: "Sazba pro {{name}}",
        amountLabel: "Částka pro {{name}}",
        removeLabel: "Odstranit řádek {{name}}",
      },
    },
    addToDraft: {
      title: "Poslat do existujícího výkazu",
      loadFailed: "Výkazy se nepodařilo načíst",
      error: "Chyba",
      added_entries_one: "{{count}} záznam přidán do výkazu",
      added_entries_few: "{{count}} záznamy přidány do výkazu",
      added_entries_other: "{{count}} záznamů přidáno do výkazu",
      added_workItems_one: "{{count}} pracovní položka přidána do výkazu",
      added_workItems_few: "{{count}} pracovní položky přidány do výkazu",
      added_workItems_other: "{{count}} pracovních položek přidáno do výkazu",
      addFailed: "Přidání do výkazu se nezdařilo",
      description_entries_one: "Přidat {{count}} záznam do otevřeného konceptu výkazu",
      description_entries_few: "Přidat {{count}} záznamy do otevřeného konceptu výkazu",
      description_entries_other: "Přidat {{count}} záznamů do otevřeného konceptu výkazu",
      description_workItems_one: "Přidat {{count}} pracovní položku do otevřeného konceptu výkazu",
      description_workItems_few: "Přidat {{count}} pracovní položky do otevřeného konceptu výkazu",
      description_workItems_other:
        "Přidat {{count}} pracovních položek do otevřeného konceptu výkazu",
      forCustomer: "pro {{customer}}",
      loading: "Načítají se výkazy…",
      lines_one: "{{count}} řádek",
      lines_few: "{{count}} řádky",
      lines_other: "{{count}} řádků",
      adding: "Přidává se…",
      empty:
        "Tento zákazník nemá žádný otevřený koncept výkazu. Použijte místo toho „Vytvořit výkaz“.",
    },
    pair: {
      title: "Spárovat s vystavenou fakturou",
      loadFailed: "Faktury se nepodařilo načíst",
      error: "Chyba",
      paired_one: "{{count}} záznam o čase spárován s fakturou",
      paired_few: "{{count}} záznamy o čase spárovány s fakturou",
      paired_other: "{{count}} záznamů o čase spárováno s fakturou",
      pairFailed: "Záznamy se nepodařilo spárovat",
      loadLinesFailed: "Řádky faktury se nepodařilo načíst",
      description_one:
        "Propojit {{count}} záznam s již vystavenou fakturou. Částky na faktuře se nemění.",
      description_few:
        "Propojit {{count}} záznamy s již vystavenou fakturou. Částky na faktuře se nemění.",
      description_other:
        "Propojit {{count}} záznamů s již vystavenou fakturou. Částky na faktuře se nemění.",
      descriptionFor_one:
        "Propojit {{count}} záznam s fakturou již vystavenou zákazníkovi {{customer}}. Částky na faktuře se nemění.",
      descriptionFor_few:
        "Propojit {{count}} záznamy s fakturou již vystavenou zákazníkovi {{customer}}. Částky na faktuře se nemění.",
      descriptionFor_other:
        "Propojit {{count}} záznamů s fakturou již vystavenou zákazníkovi {{customer}}. Částky na faktuře se nemění.",
      loading: "Načítají se faktury…",
      loadingShort: "Načítá se…",
      pairing: "Páruje se…",
      noFlatLine:
        "Tato faktura nemá paušální řádek, ke kterému by šly hodiny spárovat; její řádky se účtují z vlastních hodin nebo pracovních položek.",
      invoice: "Faktura",
      empty: "Tento zákazník nemá žádné vystavené faktury.",
    },
    builder: {
      title: "Vytvořit výkaz",
      description:
        "Vyberte zákazníka a období a pak zvolte, které pracovní položky s pevnou cenou, náklady a ruční řádky se budou účtovat. Odpracované hodiny na pracovních položkách zákazníka v režimu hodin jsou zahrnuty vždy.",
      step1: "1 · Zákazník a období",
      customer: "Zákazník",
      selectCustomer: "— vyberte zákazníka —",
      periodFrom: "Období od",
      periodTo: "Období do",
      step2: "2 · Pracovní položky s pevnou cenou",
      fixedHint:
        "Hotová práce je předzaškrtnutá; odškrtněte, co na tomto výkazu být nemá. Cena za dodání se účtuje jako řádek, jejich hodiny se zvlášť neúčtují. „Už vyfakturováno“ vyřadí pracovní položku natrvalo — nenabídne se ani na dalším výkazu.",
      noFixed: "Tento zákazník nemá žádné pracovní položky s pevnou cenou.",
      include: "Zahrnout {{name}}",
      done: "Hotovo",
      alreadyInvoiced: "Už vyfakturováno",
      alreadyInvoicedLower: "už vyfakturováno",
      step3: "3 · Náklady k zahrnutí",
      costsHint:
        "Zaškrtněte jednorázové náklady, které se mají dostat na tuto fakturu. Každý se účtuje jednou.",
      cancel: "Zrušit",
      addCost: "Přidat náklad",
      noCosts: "Tento zákazník zatím nemá žádné náklady.",
      step4: "4 · Individuální položky",
      individualHint: "Ruční řádky účtované přímo na tomto výkazu.",
      addItem: "Přidat položku",
      itemDescription: "Popis",
      itemDescriptionLabel: "Popis individuální položky {{n}}",
      itemAmountLabel: "Částka individuální položky {{n}}",
      itemRemoveLabel: "Odstranit individuální položku {{n}}",
      generating: "Vytváří se",
      generated: "Výkaz vytvořen",
      generateFailed: "Vytvoření výkazu se nezdařilo",
      pickCustomer: "Nejdřív vyberte zákazníka",
      markedInvoiced: "„{{title}}“ označeno jako už vyfakturované",
      markFailed: "Označení jako vyfakturované se nezdařilo",
      error: "Chyba",
      costAdded: "Náklad přidán",
      costAddFailed: "Náklad se nepodařilo přidat",
      costInvalid: "Zadejte popisek a kladnou částku",
      costLabel: "Popisek",
      costLabelPlaceholder: "např. Prodloužení domény",
      costAmount: "Částka",
      adding: "Přidává se",
      add: "Přidat",
    },
  },
  netlionTokens: {
    settingsTitle: "Netlion tokeny",
    settingsHint:
      "Výchozí podmínky pro účtování práce agentů: příplatek v Kč za 1 000 000 tokenů každého typu nad ceníkovou cenu a měsíční strop na zákazníka. Zákazník může každou hodnotu přepsat na své stránce úprav.",
    customerTitle: "Netlion tokeny",
    customerHint:
      "Příplatky v Kč za 1 000 000 tokenů a měsíční strop pro tohoto zákazníka. Prázdné pole použije výchozí hodnotu z nastavení účetnictví (zobrazenou šedě).",
    field: {
      tokenInputSurcharge: "Vstupní (Kč / 1 mil.)",
      tokenOutputSurcharge: "Výstupní (Kč / 1 mil.)",
      tokenCacheWriteSurcharge: "Zápis do mezipaměti (Kč / 1 mil.)",
      tokenCacheReadSurcharge: "Čtení z mezipaměti (Kč / 1 mil.)",
      tokenMonthlyCapCzk: "Měsíční strop (Kč)",
    },
  },
  aiSpend: {
    usage: {
      fiveHour: "5hodinový limit",
      sevenDay: "Týdenní limit",
      minutes: "{{min}} min",
      hoursMinutes: "{{h}} h {{min}} min",
      daysHours: "{{d}} d {{h}} h",
      noReading: "zatím bez údaje",
      reset: "obnoveno",
      resetsIn: "obnoví se za {{time}}",
    },
    hidden: {
      ui_session: "přihlášení do LiteLLM UI",
      expired: "vypršel",
      blocked: "blokován",
    },
    credentials: {
      kinds: {
        gateway_key: "Klíč brány",
        claude_token: "Předplatné Claude",
        codex_login: "Předplatné ChatGPT (Codex)",
      },
      secretHelp: {
        gateway_key: "Virtuální klíč LiteLLM z brány.",
        claude_token:
          "Z `claude setup-token`, spuštěného vlastníkem předplatného. Každé spuštění vydá nový token; tokeny jednoho účtu lze potom tady sloučit.",
        codex_login:
          "Obsah $CODEX_HOME/auth.json po `export CODEX_HOME=$(mktemp -d)` a `npx -y @openai/codex login --device-auth`. Toto přihlášení nikde jinde nepoužívejte: Hub ho neustále obnovuje.",
      },
      rename: "Přejmenovat",
      renameFailed: "Přejmenování se nezdařilo.",
      reread: "Obnovit plán",
      rereadHint:
        "Hned znovu načte limity; po navýšení plánu se agenti vrátí k práci bez čekání na reset",
      rereading: "Načítám…",
      rereadDone: "Limity načteny znovu.",
      rereadLifted: "Místo je zpět, agenti na něm zase pracují.",
      rereadFailed: "Limity se nepodařilo načíst.",
      name: "Název",
      tokenRefused: "Token odmítnut",
      waitingUntil: "Čeká do",
      active: "Aktivní",
      noAgentUses: "Nepoužívá ho žádný agent",
      paused: "pozastaven",
      saveFailed: "Uložení se nezdařilo.",
      saved: "Uloženo.",
      merged: "Sloučeno.",
      deleted: "Smazáno.",
      plan: "Tarif",
      planPlaceholder: "např. Max 20×",
      renewsOn: "Obnovuje se (měsíčně)",
      newSecret: "Nové tajemství",
      keepSecret: "nechte prázdné, ponechá se stávající",
      save: "Uložit",
      cancel: "Zrušit",
      delete: "Smazat",
      deleteConfirm: "Smazat {{name}}? Tajemství bude nenávratně pryč.",
      sameSubscriptionAs: "Stejné předplatné jako",
      mergeInto: "Sloučit do",
      pickOne: "— vyberte —",
      mergeIntoIt: "Sloučit do něj",
      mergeHint: "Jeho agenti se přesunou a ponesou token toho druhého; tento zanikne.",
      mergeFailed: "Sloučení se nezdařilo.",
      sameAccountHint:
        "Nejspíš stejné předplatné jako <1>{{name}}</1>: týdenní limit se obnovuje ve stejnou chvíli. Sloučením se jeho agenti přesunou tam.",
      merge: "Sloučit",
      planUnknown: "tarif neznámý",
      renews: "obnovuje se",
      renewalUnknown: "obnovení neznámé",
      monthSummary_one: "{{count}} běh tento měsíc · ekvivalent v API {{cost}}",
      monthSummary_few: "{{count}} běhy tento měsíc · ekvivalent v API {{cost}}",
      monthSummary_other: "{{count}} běhů tento měsíc · ekvivalent v API {{cost}}",
      read: "načteno",
      edit: "Upravit",
      thisMonth: "{{amount}} tento měsíc",
      lastMonthRuns_one: "{{amount}} minulý měsíc · {{count}} běh",
      lastMonthRuns_few: "{{amount}} minulý měsíc · {{count}} běhy",
      lastMonthRuns_other: "{{amount}} minulý měsíc · {{count}} běhů",
      runsThisMonth_one: "{{count}} běh · ~{{cost}} tento měsíc",
      runsThisMonth_few: "{{count}} běhy · ~{{cost}} tento měsíc",
      runsThisMonth_other: "{{count}} běhů · ~{{cost}} tento měsíc",
      added: "Přidáno. Agentovi ho vyberete na jeho stránce.",
      addFailed: "Přidání se nezdařilo.",
      kind: "Druh",
      namePlaceholderKey: "Název, např. Hubert",
      namePlaceholderSub: "Název, např. Claude Max (tým)",
      secret: "Tajemství",
      secretPlaceholder: "Uloží se zašifrované a už se nikdy nezobrazí",
      add: "Přidat",
      subscriptionsTitle: "Předplatná",
      subscriptionsIntro:
        "Paušální předplatná Claude a ChatGPT, na kterých agenti běží. Agent si jedno vybere na své stránce; dosažený limit nebo odmítnutý token zastaví všechny agenty na něm. Kliknutím na název ho přejmenujete.",
      noSubscription: "Není připojeno žádné předplatné.",
      keysTitle: "Klíče agentů",
      keysIntro:
        "Klíče brány, kterými agenti platí — měřené, i vedle předplatného (embeddingy, modely, které předplatné neumí). Kliknutím na název ho přejmenujete.",
      noKey: "V Hubu není uložený žádný klíč brány.",
    },
    page: {
      title: "Útrata za AI",
      subtitle:
        "Kolik spálí každý klíč brány, v Kč — a přefakturace uzavřeného měsíce namapovanému zákazníkovi (s přirážkou) jedním klikem.",
      gatewayDown: "Brána LiteLLM neodpověděla — zkontrolujte LITELLM_URL / LITELLM_ADMIN_KEY.",
      loadFailed: "Útratu za AI se nepodařilo načíst.",
      mappingSaved: "Mapování uloženo",
      mappingFailed: "Mapování se nepodařilo uložit.",
      charged: "Naúčtováno {{amount}} zákazníkovi {{customer}}",
      chargedHint: "Nákladový řádek je připravený k zařazení do měsíčního výkazu.",
      chargeFailed: "Naúčtování se nepodařilo vytvořit.",
      chargeRemoved: "Naúčtování odstraněno",
      chargeRemoveFailed: "Naúčtování se nepodařilo odstranit.",
      col: {
        key: "Klíč",
        thisMonth: "Tento měsíc",
        lastMonth: "Minulý měsíc",
        lifetime: "Celkem",
        customer: "Zákazník",
        markup: "Přirážka %",
        reinvoice: "Přefakturace",
      },
      budget: "/ rozpočet {{amount}}",
      internal: "— interní —",
      customerFor: "Zákazník pro {{key}}",
      markupFor: "Přirážka pro {{key}}",
      saveMapping: "Uložit mapování",
      chargeMonth: "Naúčtovat {{month}}",
      undo: "zpět",
      rateFallback: "záložní",
      gatewayOff:
        "Brána LiteLLM není nastavená. Nastavte <1>LITELLM_URL</1> a <3>LITELLM_ADMIN_KEY</3> (stačí klíč vytvořený s oprávněním <5>get_spend_routes</5> — nepoužívejte hlavní klíč).",
      monthAllKeys: "Tento měsíc, všechny klíče",
      lastMonthWith: "Minulý měsíc ({{month}})",
      dailySpend: "Denní útrata (60 dní, Kč)",
      syncedAt: "Útrata brány k <1/>, načítá se každých 30 minut.",
      firstRead: "Probíhá první načtení útraty z brány (pár minut); čísla se po něm doplní.",
      tab: {
        clients: "Klíče zákazníků",
        agents: "Klíče agentů",
        work: "Práce agentů",
        limits: "Spotřeba limitů",
        subscriptions: "Předplatná",
        prices: "Ceny",
      },
      noKeys: "Zatím žádné klíče brány.",
      hideKeys_one: "Skrýt {{count}} klíč, který LiteLLM nevypisuje",
      hideKeys_few: "Skrýt {{count}} klíče, které LiteLLM nevypisuje",
      hideKeys_other: "Skrýt {{count}} klíčů, které LiteLLM nevypisuje",
      showKeys_one: "Zobrazit {{count}} skrytý: {{reasons}}, bez útraty za dva měsíce",
      showKeys_few: "Zobrazit {{count}} skryté: {{reasons}}, bez útraty za dva měsíce",
      showKeys_other: "Zobrazit {{count}} skrytých: {{reasons}}, bez útraty za dva měsíce",
      clientsNote:
        'Všechny klíče brány kromě vlastních klíčů agentů (ty jsou v Klíčích agentů). Naúčtování měsíce vytvoří jednorázový náklad zákazníka („AI služby … {{month}}") — zařaďte ho do měsíčního výkazu na stránce Fakturace jako obvykle. Zpět to jde, dokud náklad nepřistane na faktuře. Správa klíčů (zakládání klíčů, pevné rozpočty) zůstává v <1>LiteLLM UI</1>.',
      notConfigured: "Brána není nastavená.",
    },
    work: {
      title: "Práce agentů podle projektů",
      month: "Měsíc",
      intro:
        'Kolik stály běhy agentů na jednotlivých projektech, oceněné podle ceníku platného v době běhu. Běhy na předplatném jsou odhad (samotné předplatné je paušální), běhy na klíči brány jsou to, co brána fakturuje, bez volání mimo běh (třeba embeddingy), proto mohou Klíče agentů ukazovat víc. Sloupec Netlion tokeny je částka pro zákazníka: dokončené běhy mimo tikety s pevnou cenou za ceníkovou cenu × kurz ČNB k poslednímu dni měsíce, plus příplatek za 1 milion tokenů každého typu. Naúčtováním vznikne jednorázový náklad zákazníka („Netlion tokeny — projekt — {{month}}"); projekty bez zákazníka naúčtovat nejde.',
      empty: "V tomto měsíci žádná práce agentů na projektech.",
      charged: "Naúčtováno",
      chargeFailed: "Naúčtování se nezdařilo",
      withdrawn: "Naúčtování staženo",
      tryAgain: "Zkuste to znovu.",
      undoFailed: "Vrácení se nezdařilo",
      invoiced: "vyfakturováno",
      undo: "Vrátit",
      charge: "Naúčtovat",
      noCustomer: "Bez zákazníka",
      col: {
        project: "Projekt",
        runs: "Běhy",
        gateway: "Brána",
        subscription: "Předplatné (odhad)",
        total: "Celkem",
        tokens: "Netlion tokeny",
        reinvoice: "Přefakturace {{month}}",
      },
      tokensHint: "Účtovaných běhů: {{runs}}, ceníková cena {{list}} + příplatky {{surcharge}}",
      cap: {
        title: "Měsíční strop Netlion tokenů",
        warning: "80 % stropu",
        over: "Nad stropem",
        line: "{{customer}}: {{amount}} z {{cap}}",
        hint: "Naúčtování se neblokuje. Překročit strop lze jen s předchozím souhlasem klienta (stačí e-mail).",
      },
    },
    limits: {
      title: "Co spotřebovává limity předplatného",
      period: "Období",
      days_one: "Poslední {{count}} den",
      days_few: "Poslední {{count}} dny",
      days_other: "Posledních {{count}} dní",
      intro:
        "Běhy na předplatném, po jednotlivých předplatných, oceněné, jako by šly přes API. Předplatné se platí paušálem, jeho limity ale ubývají zhruba podle této částky, takže čím větší podíl, tím víc limitu běh ujídá. Běhy před 27. 10. 2026 předplatné nemají zaznamenané a jsou ve zvláštní skupině. Běhy přes bránu placené za tokeny limit nejsou, proto tu chybí.",
      empty: "V tomto období žádné běhy na předplatném.",
      pool: {
        subscription: "Claude",
        codex: "ChatGPT",
      },
      unrecorded: {
        subscription: "Claude, předplatné nezaznamenané",
        codex: "ChatGPT, předplatné nezaznamenané",
      },
      poolTotal_one: "{{amount}} za {{count}} běh",
      poolTotal_few: "{{amount}} za {{count}} běhy",
      poolTotal_other: "{{amount}} za {{count}} běhů",
      tickets: "Nejdražší tikety",
      ticketsIntro:
        "Hodně běhů na jednom tiketu obvykle znamená vratky, opakování nebo dlouhá kola plánu.",
      col: {
        agent: "Agent",
        model: "Model",
        runs: "Běhy",
        share: "Podíl",
        cost: "Odhad ceny",
        wasted: "Selhané nebo přerušené",
        ticket: "Tiket",
        agents: "Agenti",
      },
      wasted_one: "{{amount}} v {{count}} běhu",
      wasted_few: "{{amount}} ve {{count}} bězích",
      wasted_other: "{{amount}} v {{count}} bězích",
    },
    totals: {
      allRuns: "Všechny běhy agentů",
      runs_one: "{{count}} běh",
      runs_few: "{{count}} běhy",
      runs_other: "{{count}} běhů",
      gateway: "Brána",
      perToken: "účtováno za token",
      subscription: "Předplatné",
      estimate: "odhad, paušální cena",
      onProjects: "Na projektech",
      canReinvoice: "lze přefakturovat",
      noProject: "Bez projektu",
      noProjectRuns_one: "{{count}} běh (pošta, skeny…)",
      noProjectRuns_few: "{{count}} běhy (pošta, skeny…)",
      noProjectRuns_other: "{{count}} běhů (pošta, skeny…)",
    },
    prices: {
      title: "Ceny modelů",
      refreshing: "Obnovuje se…",
      refreshNow: "Obnovit hned",
      refreshed_one: "Obnovena {{count}} cena modelu z brány.",
      refreshed_few: "Obnoveny {{count}} ceny modelů z brány.",
      refreshed_other: "Obnoveno {{count}} cen modelů z brány.",
      refreshFailed: "Ceny modelů se nepodařilo obnovit.",
      intro:
        "USD za milion tokenů, načtené z vlastního katalogu brány LiteLLM a obnovované denně — tímto se oceňuje každý běh agenta na této stránce. Model, který brána neocení, spadne na vestavěné sazby Hubu.",
      noneLoaded:
        "Zatím nejsou načtené žádné ceny — denní úloha ještě neběžela. Použijte Obnovit hned.",
      gatewayOff: "Brána LiteLLM není nastavená, proto Hub používá vestavěné záložní sazby.",
      empty: "Nejsou uložené žádné ceny modelů.",
      lastRefreshed: "Naposledy obnoveno <1/>",
      col: {
        model: "Model",
        input: "Vstup",
        output: "Výstup",
        cacheWrite: "Zápis do cache",
        cacheRead: "Čtení z cache",
      },
    },
  },
};
